import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { LogOut, Sun, KeyRound, Zap, QrCode, X, MoreVertical } from 'lucide-react';
import axios from 'axios';
import logoImg from '../assets/logo.png';

import { API } from '../lib/api';
import { getAuthHeaders, installAuthInterceptors } from '../lib/authClient';
import ProjectsTab from '../components/admin/projects/ProjectsTab';
import ProjectModal from '../components/admin/projects/ProjectModal';
import PartnersTab from '../components/admin/partners/PartnersTab';

installAuthInterceptors();

const TABS = { PROJECTS: 'projects', PARTNERS: 'partners' };

// ─── Admin Dashboard ───────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const t = params.get('tab');
    return Object.values(TABS).includes(t) ? t : TABS.PROJECTS;
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Project modal state
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', category: 'Residencial', power: '', image: '' });
  const [imagePreview, setImagePreview] = useState('');
  const [isDraggingImage, setIsDraggingImage] = useState(false);

  // Password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);

  // Close mobile dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) setMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Sync activeTab to URL
  const handleSetActiveTab = (tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    if (tab !== TABS.PARTNERS) params.delete('partnerTab');
    navigate({ search: params.toString() }, { replace: true });
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    fetchProjects();
  }, [navigate]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch { toast.error('Erro ao carregar projetos'); }
    finally { setIsLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logout realizado com sucesso');
    navigate('/admin/login');
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) { toast.error('As senhas não coincidem'); return; }
    if (passwordForm.new_password.length < 6) { toast.error('A nova senha deve ter pelo menos 6 caracteres'); return; }
    setIsChangingPassword(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: passwordForm.current_password, new_password: passwordForm.new_password },
        getAuthHeaders()
      );
      toast.success('Senha alterada com sucesso!');
      setShowPasswordModal(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally { setIsChangingPassword(false); }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const updateProjectImage = (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      setFormData((currentFormData) => ({ ...currentFormData, image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleImageChange = (e) => {
    updateProjectImage(e.target.files?.[0]);
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
    setIsDraggingImage(true);
  };

  const handleImageDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingImage(false);
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    setIsDraggingImage(false);
    updateProjectImage(e.dataTransfer.files?.[0]);
  };

  const openModal = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({ title: project.title, description: project.description, category: project.category, power: project.power, image: project.image });
      setImagePreview(project.image);
    } else {
      setEditingProject(null);
      setFormData({ title: '', description: '', category: 'Residencial', power: '', image: '' });
      setImagePreview('');
    }
    setIsDraggingImage(false);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProject(null);
    setImagePreview('');
    setIsDraggingImage(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, formData, getAuthHeaders());
        toast.success('Projeto atualizado com sucesso!');
      } else {
        await axios.post(`${API}/projects`, formData, getAuthHeaders());
        toast.success('Projeto criado com sucesso!');
      }
      closeModal();
      fetchProjects();
    } catch (error) {
      toast.error('Erro ao salvar projeto');
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Tem certeza que deseja excluir este projeto?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`, getAuthHeaders());
      toast.success('Projeto excluído com sucesso!');
      fetchProjects();
    } catch (error) {
      toast.error('Erro ao excluir projeto');
    }
  };

  const handleReorderProjects = async (reorderedProjects) => {
    const previousProjects = projects;
    setProjects(reorderedProjects);

    try {
      await axios.put(
        `${API}/projects/reorder`,
        reorderedProjects.map((project, index) => ({ id: project.id, order: index })),
        getAuthHeaders()
      );
      setProjects(reorderedProjects.map((project, index) => ({ ...project, order: index })));
      toast.success('Ordem dos projetos atualizada!');
    } catch (error) {
      setProjects(previousProjects);
      toast.error('Erro ao atualizar a ordem dos projetos');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ── */}
      <header className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 flex items-center justify-center">
                <img src={logoImg} alt="3M Solar" className="w-full h-full object-contain" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Dashboard Admin</h1>
                <p className="text-sm text-gray-600">3M Solar</p>
              </div>
            </div>

            {/* Desktop buttons */}
            <div className="hidden sm:flex items-center space-x-3">
              <Button onClick={() => navigate('/')} variant="outline" className="border-gray-300">Ver Site</Button>
              <Button onClick={() => setShowPasswordModal(true)} variant="outline" className="border-orange-400 text-orange-600 hover:bg-orange-50">
                <KeyRound className="w-4 h-4 mr-2" />Mudar Senha
              </Button>
              <Button onClick={handleLogout} className="bg-red-600 hover:bg-red-700 text-white">
                <LogOut className="w-4 h-4 mr-2" />Sair
              </Button>
            </div>

            {/* Mobile dropdown trigger */}
            <div className="relative sm:hidden" ref={mobileMenuRef}>
              <button
                onClick={() => setMobileMenuOpen(v => !v)}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                aria-label="Menu"
              >
                <MoreVertical className="w-5 h-5 text-gray-600" />
              </button>
              {mobileMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden">
                  <button
                    onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                    className="flex items-center gap-2 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Sun className="w-4 h-4 text-gray-500" />Ver Site
                  </button>
                  <button
                    onClick={() => { setShowPasswordModal(true); setMobileMenuOpen(false); }}
                    className="flex items-center gap-2 w-full px-4 py-3 text-sm text-orange-600 hover:bg-orange-50 transition-colors"
                  >
                    <KeyRound className="w-4 h-4" />Mudar Senha
                  </button>
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-2 w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100"
                  >
                    <LogOut className="w-4 h-4" />Sair
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Main tabs */}
          <div className="flex gap-1 mt-4 border-b border-gray-200">
            <button
              onClick={() => handleSetActiveTab(TABS.PROJECTS)}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === TABS.PROJECTS ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <Zap className="w-4 h-4 inline mr-1.5 -mt-0.5" />Projetos
            </button>
            <button
              onClick={() => handleSetActiveTab(TABS.PARTNERS)}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === TABS.PARTNERS ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              <QrCode className="w-4 h-4 inline mr-1.5 -mt-0.5" />Parcerias
            </button>
          </div>
        </div>
      </header>

      {/* ── Main content ── */}
      <main className="container mx-auto px-4 py-8">
        {activeTab === TABS.PROJECTS && (
          <ProjectsTab
            projects={projects}
            openModal={openModal}
            handleDelete={handleDelete}
            handleReorderProjects={handleReorderProjects}
          />
        )}
        {activeTab === TABS.PARTNERS && (
          <PartnersTab getAuthHeaders={getAuthHeaders} navigate={navigate} location={location} />
        )}
      </main>

      {/* ── Project Modal ── */}
      {showModal && (
        <ProjectModal
          editingProject={editingProject}
          formData={formData}
          imagePreview={imagePreview}
          onClose={closeModal}
          onSubmit={handleSubmit}
          onChange={handleChange}
          onImageChange={handleImageChange}
          onImageDragOver={handleImageDragOver}
          onImageDragLeave={handleImageDragLeave}
          onImageDrop={handleImageDrop}
          isDraggingImage={isDraggingImage}
          onRemoveImage={() => {
            setImagePreview('');
            setFormData((currentFormData) => ({ ...currentFormData, image: '' }));
            setIsDraggingImage(false);
          }}
        />
      )}

      {/* ── Change Password Modal ── */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-orange-600" />Mudar Senha
                </h3>
                <button
                  onClick={() => { setShowPasswordModal(false); setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Senha Atual</label>
                  <Input
                    type="password" value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                    required placeholder="Digite sua senha atual"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nova Senha</label>
                  <Input
                    type="password" value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    required placeholder="Mínimo 6 caracteres"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar Nova Senha</label>
                  <Input
                    type="password" value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                    required placeholder="Repita a nova senha"
                  />
                </div>
                <div className="flex space-x-3 pt-2">
                  <Button
                    type="button"
                    onClick={() => { setShowPasswordModal(false); setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); }}
                    variant="outline" className="flex-1" disabled={isChangingPassword}
                  >
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" disabled={isChangingPassword}>
                    {isChangingPassword ? 'Salvando...' : 'Salvar Senha'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
